import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Loader2, MailCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setStep(2);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code: code.trim(), new_password: newPassword });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-5 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/" className="mb-10 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#00E676]">
            <Zap className="h-5 w-5 text-black" fill="black" />
          </span>
          <span className="font-display text-xl font-black tracking-tight">TipNu</span>
        </Link>

        {step === 1 ? (
          <>
            <h1 className="font-display text-4xl font-black tracking-tight">Forgot password?</h1>
            <p className="mt-2 text-zinc-400">Enter your email and we'll send you a 6-digit reset code.</p>
            <form onSubmit={requestCode} className="mt-8 space-y-5" data-testid="forgot-password-form">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Email</Label>
                <Input
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2 h-12 border-white/10 bg-[#0F0F0F] text-white focus-visible:ring-[#00E676]"
                />
              </div>
              {error && <p data-testid="forgot-error" className="text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                data-testid="forgot-submit-btn"
                disabled={loading}
                className="flex h-12 w-full items-center justify-center rounded-full bg-[#00E676] font-bold text-black transition-transform active:scale-95 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset code"}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="mb-2 flex items-center gap-2 text-[#00E676]">
              <MailCheck className="h-5 w-5" />
              <span className="text-sm font-semibold">Check your email</span>
            </div>
            <h1 className="font-display text-4xl font-black tracking-tight">Enter your code</h1>
            <p className="mt-2 text-zinc-400">
              We sent a 6-digit code to <span className="text-white">{email}</span> (if an account exists). It expires in 15 minutes.
            </p>
            <form onSubmit={reset} className="mt-8 space-y-5" data-testid="reset-password-form">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Reset code</Label>
                <Input
                  data-testid="reset-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  required
                  placeholder="123456"
                  className="mt-2 h-12 border-white/10 bg-[#0F0F0F] text-center text-2xl font-bold tracking-[0.4em] text-white focus-visible:ring-[#00E676]"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">New password</Label>
                <Input
                  data-testid="reset-newpassword-input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="mt-2 h-12 border-white/10 bg-[#0F0F0F] text-white focus-visible:ring-[#00E676]"
                />
              </div>
              {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                data-testid="reset-submit-btn"
                disabled={loading}
                className="flex h-12 w-full items-center justify-center rounded-full bg-[#00E676] font-bold text-black transition-transform active:scale-95 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Reset password"}
              </button>
              <button
                type="button"
                onClick={() => { setStep(1); setError(""); }}
                className="w-full text-sm text-zinc-500 hover:text-white"
              >
                Use a different email
              </button>
            </form>
          </>
        )}

        <p className="mt-6 text-sm text-zinc-500">
          Remembered it?{" "}
          <Link to="/login" data-testid="forgot-to-login" className="font-semibold text-[#00E676]">Log in</Link>
        </p>
      </motion.div>
    </div>
  );
}
